
import { useState, memo, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight, X } from "lucide-react";
import { ContactButton } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Sheet, SheetContent, SheetClose } from "@/components/ui/sheet";
import { useMenuToggle } from "@/hooks/useMenuToggle";

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Improved mobile navigation menu component that fills the entire screen
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the mobile menu is open
 * @param {Function} props.onClose - Function to close the mobile menu
 * @returns {JSX.Element} The MobileMenu component
 */
export const MobileMenu = memo(({ isOpen, onClose }: MobileMenuProps) => {
  const location = useLocation();

  // Automatically expand services and specializations sections for better visibility
  const [servicesOpen, setServicesOpen] = useState(true);
  const [specializationsOpen, setSpecializationsOpen] = useState(true);

  // Memoized isActive function to avoid unnecessary re-renders
  const isActive = useCallback((path: string) => {
    return location.pathname === path;
  }, [location.pathname]);

  // Optimized toggle handlers
  const toggleServices = useCallback(() => {
    setServicesOpen(prev => !prev);
  }, []);

  const toggleSpecializations = useCallback(() => {
    setSpecializationsOpen(prev => !prev);
  }, []);

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent 
        side="right" 
        className="w-full sm:max-w-full p-0 bg-white"
      >
        <div className="flex flex-col h-full overflow-auto">
          <div className="sticky top-0 flex items-center justify-between p-4 border-b border-gray-100 bg-white z-10">
            <div className="flex-1">
              <img
                src="/lovable-uploads/6e88f8b3-0ddf-4ad8-9625-adcf5c8d9671.png"
                alt="EdgeFi Logo"
                className="h-10 w-auto"
              />
            </div>
            <SheetClose className="rounded-full h-10 w-10 flex items-center justify-center hover:bg-gray-100">
              <X className="h-6 w-6 text-gray-700" />
              <span className="sr-only">Close</span>
            </SheetClose>
          </div>
          
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Services Section */}
            <div className="space-y-4">
              <button
                onClick={toggleServices}
                className={cn(
                  "flex items-center justify-between w-full py-3 px-4 rounded-xl transition-colors font-medium text-xl",
                  servicesOpen 
                    ? "bg-edgefi-purple bg-opacity-5 text-edgefi-purple" 
                    : "text-gray-700 hover:text-edgefi-purple"
                )}
              >
                <span>Services</span>
              </button>
              
              {servicesOpen && (
                <div className="pl-4 space-y-4">
                  <Link
                    to="/services/cybersecurity-advisory"
                    className={cn(
                      "block py-3.5 px-4 text-base rounded-lg transition-all",
                      isActive("/services/cybersecurity-advisory")
                        ? "bg-edgefi-purple bg-opacity-5 text-edgefi-purple font-medium"
                        : "text-gray-700 hover:text-edgefi-purple hover:bg-gray-50"
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <span>Cybersecurity Advisory</span>
                      <ChevronRight className="h-5 w-5 opacity-70" />
                    </div>
                  </Link>
                  <Link
                    to="/services/managed-cybersecurity"
                    className={cn(
                      "block py-3.5 px-4 text-base rounded-lg transition-all",
                      isActive("/services/managed-cybersecurity")
                        ? "bg-edgefi-purple bg-opacity-5 text-edgefi-purple font-medium"
                        : "text-gray-700 hover:text-edgefi-purple hover:bg-gray-50"
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <span>Managed Cybersecurity Services</span>
                      <ChevronRight className="h-5 w-5 opacity-70" />
                    </div>
                  </Link>
                  <Link
                    to="/services/managed-services"
                    className={cn(
                      "block py-3.5 px-4 text-base rounded-lg transition-all",
                      isActive("/services/managed-services")
                        ? "bg-edgefi-purple bg-opacity-5 text-edgefi-purple font-medium"
                        : "text-gray-700 hover:text-edgefi-purple hover:bg-gray-50"
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <span>Managed IT Services</span>
                      <ChevronRight className="h-5 w-5 opacity-70" />
                    </div>
                  </Link>
                </div>
              )}
            </div>
            
            {/* Specializations Section */}
            <div className="space-y-4">
              <button
                onClick={toggleSpecializations}
                className={cn(
                  "flex items-center justify-between w-full py-3 px-4 rounded-xl transition-colors font-medium text-xl",
                  specializationsOpen 
                    ? "bg-edgefi-purple bg-opacity-5 text-edgefi-purple" 
                    : "text-gray-700 hover:text-edgefi-purple"
                )}
              >
                <span>Specializations</span>
              </button>
              
              {specializationsOpen && (
                <div className="pl-4 space-y-4">
                  <Link
                    to="/specializations/microsoft-365-security"
                    className={cn(
                      "block py-3.5 px-4 text-base rounded-lg transition-all",
                      isActive("/specializations/microsoft-365-security")
                        ? "bg-edgefi-purple bg-opacity-5 text-edgefi-purple font-medium"
                        : "text-gray-700 hover:text-edgefi-purple hover:bg-gray-50"
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <span>Microsoft 365 Security</span>
                      <ChevronRight className="h-5 w-5 opacity-70" />
                    </div>
                  </Link>
                  <Link
                    to="/specializations/microsoft-365-engineering"
                    className={cn(
                      "block py-3.5 px-4 text-base rounded-lg transition-all",
                      isActive("/specializations/microsoft-365-engineering")
                        ? "bg-edgefi-purple bg-opacity-5 text-edgefi-purple font-medium"
                        : "text-gray-700 hover:text-edgefi-purple hover:bg-gray-50"
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <span>Microsoft 365 Engineering</span>
                      <ChevronRight className="h-5 w-5 opacity-70" />
                    </div>
                  </Link>
                </div>
              )}
            </div>
            
            {/* About Us Link */}
            <div>
              <Link
                to="/about"
                className={cn(
                  "block py-3.5 px-4 text-xl rounded-xl transition-colors",
                  isActive("/about")
                    ? "bg-edgefi-purple bg-opacity-5 text-edgefi-purple font-medium"
                    : "text-gray-700 hover:text-edgefi-purple hover:bg-gray-50"
                )}
              >
                About Us
              </Link>
            </div>
          </div>
          
          {/* Contact Us Button */}
          <div className="p-6 border-t border-gray-100">
            <ContactButton 
              modalTitle="Contact Us"
              modalDescription="Have questions? Get in touch with our team."
              variant="cta" 
              className="w-full rounded-full bg-gradient-to-r from-edgefi-purple to-purple-600 text-white font-medium py-6 h-auto"
            >
              Contact Us
            </ContactButton>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
});

MobileMenu.displayName = "MobileMenu";
